import { useEffect, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, AreaChart, Area,
  PieChart, Pie, Cell, CartesianGrid,
} from "recharts";
import { Users, UserCheck, Gauge, TrendingUp, Clock, AlertTriangle, CalendarCheck2, Code2, ClipboardList, Percent } from "lucide-react";
import { api } from "../lib/apiClient";
import { useApp, MONTHS } from "../lib/store";
import { StatCard, PageHeader, GradeBadge, Avatar, Empty } from "../components/Shared";
import { Panel } from "../components/Filters";
import { scoreColor, GRADE_SCALE } from "../lib/constants";

const AXIS = "#64748B";
function ChartTip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="card-panel px-3 py-2 text-xs" style={{ boxShadow: "0 8px 24px rgba(0,0,0,0.3)" }}>
      <p className="font-semibold mb-1">{label}</p>
      {payload.map((p, i) => <p key={i} style={{ color: p.color || p.fill }} className="mono font-semibold">{p.name}: {p.value}</p>)}
    </div>
  );
}

export default function Dashboard() {
  const { month, year } = useApp();
  const [data, setData] = useState(null);
  const monthName = MONTHS.find((m) => m.v === month)?.n;

  useEffect(() => { api.dashboard(month, year).then(setData).catch(() => {}); }, [month, year]);

  if (!data) return <div className="py-20 text-center text-muted-c">Loading dashboard…</div>;
  const k = data.kpis, c = data.charts;

  return (
    <div>
      <PageHeader title="Management Dashboard" subtitle={`${monthName} ${year} · Team performance command center`}
        exportModule="dashboard" exportParams={{ month, year }} />

      <div className="card-panel p-4 mb-4" data-testid="grade-scale-legend">
        <p className="text-xs font-semibold text-muted-c uppercase tracking-wider mb-3">Performance Grade Scale · how points are distributed</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {GRADE_SCALE.map((g) => (
            <div key={g.grade} className="elevated rounded-xl p-3 flex items-center gap-3 border border-subtle" data-testid={`grade-scale-${g.grade}`} style={{ borderLeft: `4px solid ${g.color}` }}>
              <div className="grade-ring w-10 h-10 rounded-lg text-lg" style={{ background: `${g.color}22`, color: g.color }}>{g.grade}</div>
              <div className="min-w-0">
                <p className="font-semibold text-sm" style={{ color: g.color }}>{g.label}</p>
                <p className="mono text-xs text-muted-c">{g.range}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <StatCard testid="kpi-card-total-employees" label="Total Employees" value={k.total_employees} sub={`${k.active_employees} active`} icon={Users} accent="#2563EB" />
        <StatCard testid="kpi-card-avg-kra" label="Avg Team Score" value={k.avg_team_score} sub={`out of 100 · ${monthName}`} icon={Gauge} accent="#10B981" />
        <StatCard testid="kpi-card-on-time-rate" label="Tasks On Time" value={k.tasks_on_time} sub={`${k.tasks_delayed} delayed · ${k.total_tasks} total`} icon={Clock} accent="#F59E0B" />
        <StatCard testid="kpi-card-increment-est" label="Avg Increment Est." value={`${k.avg_increment}%`} sub="recommended" icon={Percent} accent="#818CF8" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Top Performer" value={k.top_performer?.name?.split(" ")[0] || "-"} sub={k.top_performer ? `${k.top_performer.month_score} · ${k.top_performer.grade}` : ""} icon={TrendingUp} accent="#10B981" />
        <StatCard label="Needs Attention" value={k.lowest_performer?.name?.split(" ")[0] || "-"} sub={k.lowest_performer ? `${k.lowest_performer.month_score} · ${k.lowest_performer.grade}` : ""} icon={AlertTriangle} accent="#EF4444" />
        <StatCard label="CC Compliance" value={`${k.cc_compliance}%`} sub="Content Calendar" icon={CalendarCheck2} accent="#38BDF8" />
        <StatCard label="Pending Reviews" value={k.pending_reviews} sub={`${k.dev_compliance}% dev compliance`} icon={ClipboardList} accent="#F59E0B" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-6">
        <div className="lg:col-span-8">
          <Panel title="Employee Performance Comparison" testid="chart-comparison">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={c.comparison} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" vertical={false} />
                <XAxis dataKey="name" tick={{ fill: AXIS, fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis domain={[0, 100]} tick={{ fill: AXIS, fontSize: 12 }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTip />} cursor={{ fill: "rgba(37,99,235,0.08)" }} />
                <Bar dataKey="score" radius={[6, 6, 0, 0]} maxBarSize={54} isAnimationActive={false}>
                  {c.comparison.map((e, i) => <Cell key={i} fill={scoreColor(e.score)} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Panel>
        </div>
        <div className="lg:col-span-4">
          <Panel title="On-Time vs Delayed" testid="chart-ontime">
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={c.on_time_vs_delayed} dataKey="value" nameKey="name" innerRadius={62} outerRadius={95} paddingAngle={3} isAnimationActive={false}>
                  <Cell fill="#10B981" /><Cell fill="#EF4444" />
                </Pie>
                <Tooltip content={<ChartTip />} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex justify-center gap-5 -mt-2">
              <span className="flex items-center gap-1.5 text-xs"><span className="w-2.5 h-2.5 rounded-full" style={{ background: "#10B981" }} />On Time</span>
              <span className="flex items-center gap-1.5 text-xs"><span className="w-2.5 h-2.5 rounded-full" style={{ background: "#EF4444" }} />Delayed</span>
            </div>
          </Panel>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 space-y-6">
          <Panel title="Monthly Team Performance Trend" testid="chart-trend">
            {c.trend.length ? (
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={c.trend} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <defs><linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#2563EB" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#2563EB" stopOpacity={0} />
                  </linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" vertical={false} />
                  <XAxis dataKey="month" tick={{ fill: AXIS, fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis domain={[0, 100]} tick={{ fill: AXIS, fontSize: 12 }} axisLine={false} tickLine={false} />
                  <Tooltip content={<ChartTip />} />
                  <Area type="monotone" dataKey="score" stroke="#2563EB" strokeWidth={2.5} fill="url(#g1)" isAnimationActive={false} />
                </AreaChart>
              </ResponsiveContainer>
            ) : <Empty />}
          </Panel>
          <Panel title="Quarterly Performance" testid="chart-quarterly">
            {c.quarters.length ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={c.quarters} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" vertical={false} />
                  <XAxis dataKey="quarter" tick={{ fill: AXIS, fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis domain={[0, 100]} tick={{ fill: AXIS, fontSize: 12 }} axisLine={false} tickLine={false} />
                  <Tooltip content={<ChartTip />} cursor={{ fill: "rgba(37,99,235,0.08)" }} />
                  <Bar dataKey="score" fill="#818CF8" radius={[6, 6, 0, 0]} maxBarSize={70} isAnimationActive={false} />
                </BarChart>
              </ResponsiveContainer>
            ) : <Empty />}
          </Panel>
        </div>
        <div className="lg:col-span-4">
          <Panel title="Employee Ranking" testid="widget-ranking">
            <div className="space-y-3">
              {c.ranking.map((r) => (
                <div key={r.name} className="flex items-center gap-3">
                  <span className="mono font-bold text-sm w-5 text-muted-c">#{r.rank}</span>
                  <Avatar src={r.profile_image} name={r.name} size={38} />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{r.name}</p>
                    <p className="text-xs text-muted-c truncate">{r.designation}</p>
                  </div>
                  <div className="text-right">
                    <p className="mono font-bold text-sm">{r.score}</p>
                    <GradeBadge grade={r.grade} />
                  </div>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}
